
import Image from '@/components/compat/Image';
import { BookOpen, Clock, Eye } from 'lucide-react';
import { StaffXatRankBadge } from '@/components/help-center/equipe-shared';
import { ORBITAL_ICON_BOX_STYLE, cn } from '@/components/help-center/latest-power-shared';
import {
  formatArticleDateTime,
  type ArticleAuthor,
  type ArticleAuthorCardProps,
} from '@/lib/article-author';
import { formatReadingTimeLabel } from '@/utils/readingTime';
import { formatViewCount } from '@/utils/formatViews';

/** Shell escuro alinhado à Powerpédia / filtros do portal — sem imagem de fundo. */
export const articleAuthorCardShellClass = cn(
  'relative overflow-hidden rounded-[20px] border border-blue-900/60',
  'bg-[#030023] shadow-lg shadow-black/25 ring-1 ring-sky-400/20',
  'shadow-[0_0_40px_-18px_rgba(56,189,248,0.28)]'
);

function ArticleAuthorCardBackdrop() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#000b6a]/50 via-[#030023] to-[#01012e]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-sky-500/[0.05] via-transparent to-[#030023]/90"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/30 to-transparent"
        aria-hidden
      />
    </>
  );
}

function AuthorAvatar({ author }: { author: ArticleAuthor }) {
  if (author.avatarUrl) {
    return (
      <div
        className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 ring-sky-400/30 ring-offset-2 ring-offset-[#030023]"
        style={ORBITAL_ICON_BOX_STYLE}
      >
        <Image
          src={author.avatarUrl}
          alt=""
          fill
          className="object-cover"
          sizes="56px"
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full font-poppins text-base font-bold text-sky-100 ring-2 ring-sky-400/30 ring-offset-2 ring-offset-[#030023]"
      style={ORBITAL_ICON_BOX_STYLE}
      aria-hidden
    >
      {author.avatarInitials}
    </div>
  );
}

type MetaBlockProps = {
  icon: typeof Clock;
  label: string;
  value: string;
};

function MetaBlock({ icon: Icon, label, value }: MetaBlockProps) {
  return (
    <div
      className={cn(
        'flex min-w-[8.75rem] items-start gap-2.5 rounded-xl',
        'border border-white/[0.08] bg-white/[0.04] px-3 py-2.5',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-sm',
        'transition-colors duration-200 hover:border-sky-400/20 hover:bg-white/[0.06]'
      )}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 ring-1 ring-sky-400/20"
        aria-hidden
      >
        <Icon className="h-3.5 w-3.5 text-sky-300" />
      </div>
      <div className="min-w-0 pt-0.5">
        <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-200/75">
          {label}
        </p>
        <p className="mt-0.5 font-inter text-sm font-medium leading-snug text-blue-50">
          {value}
        </p>
      </div>
    </div>
  );
}

export function ArticleAuthorCard({
  author,
  publishedAt,
  readingTimeMinutes,
  views = 0,
  className,
}: ArticleAuthorCardProps) {
  return (
    <div className={cn(articleAuthorCardShellClass, className)}>
      <ArticleAuthorCardBackdrop />

      <div className="relative z-10 flex flex-col gap-5 p-5 sm:p-6 md:gap-6 lg:flex-row lg:items-stretch lg:justify-between">
        <div className="flex min-w-0 items-start gap-4 lg:flex-1">
          <AuthorAvatar author={author} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-poppins text-lg font-bold text-white sm:text-xl">
                {author.name}
              </h2>
              {author.officialRank ? (
                <StaffXatRankBadge rank={author.officialRank} />
              ) : null}
            </div>
            {author.helpCenterRoleLabel ? (
              <p className="mt-0.5 font-inter text-sm text-blue-100/80">
                {author.helpCenterRoleLabel}
              </p>
            ) : null}
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col gap-2.5 border-t border-white/[0.08] pt-5',
            'sm:flex-row sm:flex-wrap sm:items-stretch sm:gap-3 sm:border-t-0 sm:pt-0',
            'lg:shrink-0 lg:justify-end'
          )}
        >
          {publishedAt ? (
            <MetaBlock
              icon={Clock}
              label="Publicado"
              value={formatArticleDateTime(publishedAt)}
            />
          ) : null}
          <MetaBlock
            icon={BookOpen}
            label="Leitura"
            value={formatReadingTimeLabel(readingTimeMinutes)}
          />
          <MetaBlock icon={Eye} label="Visualizações" value={formatViewCount(views)} />
        </div>
      </div>
    </div>
  );
}
